import { Edit, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useGo } from "@refinedev/core";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  DatePicker,
  Row,
  Col,
  Card,
  Space,
  Alert,
  App,
  Button,
} from "antd";
import {
  InfoCircleOutlined,
  WarningOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import type {
  ControlPointDetail,
  ControlPointEditFormValues,
  ControlPointStatus,
} from "../../types/control-point";
import dayjs from "dayjs";
import "./control-points.css";

const { TextArea } = Input;

/**
 * ControlPointEdit Component
 * Form for editing an existing control point
 * Allows editing basic fields and marking as converted
 * Validates that conversion automatically closes the control point
 */
export const ControlPointEdit = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const go = useGo();

  const [isConverted, setIsConverted] = useState(false);
  const [originalStatus, setOriginalStatus] = useState<ControlPointStatus>("open");

  const { formProps, saveButtonProps, query, form } = useForm<
    ControlPointDetail,
    HttpError,
    ControlPointEditFormValues
  >({
    action: "edit",
    redirect: "show",
  });

  const controlPoint = query?.data?.data;

  // Initialize state when data loads
  useEffect(() => {
    if (controlPoint) {
      setOriginalStatus(controlPoint.status);
      setIsConverted(!!controlPoint.conversion_date);
    }
  }, [controlPoint]);

  // Watch for conversion date changes
  const conversionDate = Form.useWatch("conversion_date", form);
  const convertedHectares = Form.useWatch("converted_hectares", form);

  /**
   * Automatically set status to closed when conversion is marked
   * Validates: Requirement 2.5 - automatic status change on conversion
   */
  useEffect(() => {
    if (conversionDate && convertedHectares && convertedHectares > 0) {
      // Automatically set status to closed when converted
      form.setFieldValue("status", "closed");
      setIsConverted(true);
    } else if (!conversionDate || !convertedHectares || convertedHectares === 0) {
      // Reset to original status if conversion is cleared
      if (isConverted && !controlPoint?.conversion_date) {
        form.setFieldValue("status", originalStatus);
      }
      setIsConverted(false);
    }
  }, [conversionDate, convertedHectares, form, originalStatus, isConverted, controlPoint]);

  /**
   * Handle form submission
   */
  const handleFinish = async (values: ControlPointEditFormValues) => {
    try {
      // Format dates to YYYY-MM-DD if they exist
      const formattedValues = { ...values };
      
      if (formattedValues.closed_date && dayjs.isDayjs(formattedValues.closed_date)) {
        formattedValues.closed_date = formattedValues.closed_date.format("YYYY-MM-DD");
      }
      
      if (formattedValues.conversion_date && dayjs.isDayjs(formattedValues.conversion_date)) {
        formattedValues.conversion_date = formattedValues.conversion_date.format("YYYY-MM-DD");
      }

      // Validate conversion logic
      if (formattedValues.conversion_date && (!formattedValues.converted_hectares || formattedValues.converted_hectares <= 0)) {
        message.error(
          t(
            "controlPoint.error.conversionRequiresHectares",
            "Debe especificar las hectáreas convertidas"
          )
        );
        return;
      }

      if (formattedValues.converted_hectares && formattedValues.converted_hectares > 0 && !formattedValues.conversion_date) {
        message.error(
          t(
            "controlPoint.error.hectaresRequireConversionDate",
            "Debe especificar la fecha de conversión"
          )
        );
        return;
      }

      // Ensure status is closed when converted
      if (formattedValues.conversion_date && formattedValues.converted_hectares && formattedValues.converted_hectares > 0) {
        formattedValues.status = "closed";
      }

      return formattedValues;
    } catch (error: any) {
      message.error(
        error.message ||
          t("controlPoint.update_error", "Error al actualizar el punto de control")
      );
      throw error;
    }
  };

  if (!controlPoint) {
    return null;
  }

  return (
    <Edit
      title={t("controlPoint.edit", "Editar Punto de Control")}
      saveButtonProps={saveButtonProps}
      headerButtons={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => go({ to: "/control-points" })}
        >
          {t("common.back", "Volver")}
        </Button>
      }
      footerButtons={
        <Space>
          <Button onClick={() => go({ to: "/control-points" })}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            {...saveButtonProps}
            type="primary"
            icon={<SaveOutlined />}
          >
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      }
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
      >
        {/* Alert for conversion auto-close */}
        {(conversionDate || convertedHectares) && (
          <Alert
            message={t("controlPoint.conversionWarning", "Conversión a Superficie Licenciada")}
            description={t(
              "controlPoint.conversionWarningDescription",
              "Al marcar un punto de control como convertido, su estado cambiará automáticamente a 'Cerrado'."
            )}
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Card
          title={t("controlPoint.basicInfo", "Información Básica")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.name", "Nombre")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: `${t("controlPoint.name")} ${t("common.required")}`,
                  },
                  {
                    max: 100,
                    message: "El nombre no puede exceder 100 caracteres",
                  },
                ]}
              >
                <Input
                  placeholder={t(
                    "controlPoint.namePlaceholder",
                    "Ej: CP-001"
                  )}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.waypointName", "Nombre Waypoint")}
                name="waypoint_name"
                tooltip={t(
                  "controlPoint.waypointNameTooltip",
                  "Nombre del waypoint GPS (opcional)"
                )}
              >
                <Input
                  placeholder={t(
                    "controlPoint.waypointNamePlaceholder",
                    "Ej: WP001"
                  )}
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.status.title", "Estado")}
                name="status"
                rules={[
                  {
                    required: true,
                    message: `${t("controlPoint.status.title")} ${t("common.required")}`,
                  },
                ]}
                tooltip={
                  isConverted
                    ? t(
                        "controlPoint.statusLockedTooltip",
                        "El estado se establece automáticamente a 'Cerrado' cuando se marca como convertido"
                      )
                    : undefined
                }
              >
                <Select
                  options={[
                    {
                      label: t("controlPoint.status.open", "Abierto"),
                      value: "open",
                    },
                    {
                      label: t("controlPoint.status.closed", "Cerrado"),
                      value: "closed",
                    },
                  ]}
                  disabled={isConverted}
                  placeholder={t(
                    "controlPoint.statusPlaceholder",
                    "Seleccione el estado"
                  )}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.closedDate", "Fecha de Cierre")}
                name="closed_date"
                tooltip={t(
                  "controlPoint.closedDateTooltip",
                  "Fecha en que se cerró el punto de control (opcional)"
                )}
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : undefined,
                })}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={t("common.selectDate", "Seleccione fecha")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t("controlPoint.commentClosed", "Comentario de Cierre")}
            name="comment_closed"
            tooltip={t(
              "controlPoint.commentClosedTooltip",
              "Comentario explicando el motivo del cierre (opcional)"
            )}
          >
            <TextArea
              rows={3}
              placeholder={t(
                "controlPoint.commentClosedPlaceholder",
                "Describa el motivo del cierre..."
              )}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Card>

        <Card
          title={
            <Space>
              <WarningOutlined />
              {t("controlPoint.conversionInfo", "Información de Conversión")}
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message={t("controlPoint.conversionNote", "Nota sobre Conversión")}
            description={t(
              "controlPoint.conversionNoteDescription",
              "Use esta sección para registrar cuando un punto de control se convierte en superficie licenciada. Al completar estos campos, el punto de control se cerrará automáticamente."
            )}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.conversionDate", "Fecha de Conversión")}
                name="conversion_date"
                tooltip={t(
                  "controlPoint.conversionDateTooltip",
                  "Fecha en que el punto de control se convirtió en superficie licenciada"
                )}
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : undefined,
                })}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={t("common.selectDate", "Seleccione fecha")}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.convertedHectares", "Hectáreas Convertidas")}
                name="converted_hectares"
                tooltip={t(
                  "controlPoint.convertedHectaresTooltip",
                  "Número de hectáreas que se convirtieron en superficie licenciada"
                )}
                rules={[
                  {
                    type: "number",
                    min: 0,
                    message: "Las hectáreas deben ser un valor positivo",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Ej: 2.5"
                  min={0}
                  step={0.1}
                  precision={2}
                  addonAfter="ha"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t(
              "controlPoint.authorizedFarmReference",
              "Referencia Finca/Parcela Autorizada"
            )}
            name="authorized_farm_reference"
            tooltip={t(
              "controlPoint.authorizedFarmReferenceTooltip",
              "Referencia o código de la finca o parcela resultante (opcional)"
            )}
          >
            <Input
              placeholder={t(
                "controlPoint.authorizedFarmReferencePlaceholder",
                "Ej: FARM-2024-001 o Parcela 123"
              )}
              maxLength={200}
            />
          </Form.Item>
        </Card>
      </Form>
    </Edit>
  );
};
